// HelpView — auth-gated in-app user guide rendered when view === 'help'.
// Uses /api/help/* (role-filtered server-side via JWT).

import { useEffect, useState, useCallback, useRef } from "react";
import { T } from "../theme";
import { apiCall } from "../apiClient";

type ArticleListItem = {
  slug: string;
  title: string;
  section: string;
  section_order: number;
  article_order: number;
  is_public?: boolean;
};

type ArticleDetail = {
  slug: string;
  title: string;
  section: string;
  body_markdown: string;
  body_html: string;
  updated_at: string;
};

type SearchResult = {
  slug: string;
  title: string;
  section: string;
  rank: number;
  snippet: string;
};

export const HelpView = ({ initialSlug }: { initialSlug?: string | null }) => {
  const [articles, setArticles] = useState<ArticleListItem[]>([]);
  const [activeSlug, setActiveSlug] = useState<string | null>(initialSlug || null);
  const [activeArticle, setActiveArticle] = useState<ArticleDetail | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[] | null>(null);
  const [loading, setLoading] = useState(true);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiCall("/help/articles");
        if (cancelled) return;
        const list: ArticleListItem[] = r?.articles || [];
        setArticles(list);
        setActiveSlug(prev => prev || (list.length > 0 ? list[0].slug : null));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!activeSlug) { setActiveArticle(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await apiCall(`/help/articles/${encodeURIComponent(activeSlug)}`);
        if (!cancelled && r?.article) setActiveArticle(r.article);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [activeSlug]);

  useEffect(() => {
    if (searchQuery.trim().length < 2) { setSearchResults(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await apiCall(`/help/search?q=${encodeURIComponent(searchQuery)}`);
        setSearchResults(r?.results || []);
      } catch { setSearchResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = document.activeElement?.tagName;
      if (e.key === "/" && tag !== "INPUT" && tag !== "TEXTAREA") {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const onSelectArticle = useCallback((slug: string) => {
    setActiveSlug(slug);
    setSearchQuery("");
    setSearchResults(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const sectionGroups = articles.reduce((acc, a) => {
    if (!acc[a.section]) acc[a.section] = [];
    acc[a.section].push(a);
    return acc;
  }, {} as Record<string, ArticleListItem[]>);

  return (
    <div data-deploy-marker="wave6-help-view" style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: 24, padding: 20 }}>
      <aside>
        <input
          ref={searchInputRef}
          type="search"
          placeholder="Search the guide... (press /)"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, fontFamily: "inherit", marginBottom: 16, boxSizing: "border-box" }}
        />

        {searchResults !== null ? (
          <div>
            <div style={{ fontSize: 11, color: T.txM, textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 8 }}>Results ({searchResults.length})</div>
            {searchResults.length === 0 && <div style={{ fontSize: 12, color: T.txM }}>No results.</div>}
            {searchResults.map(r => (
              <button key={r.slug} onClick={() => onSelectArticle(r.slug)} style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 10px", borderRadius: 4, border: "none", backgroundColor: r.slug === activeSlug ? T.accS : "transparent", color: T.tx, fontSize: 12, cursor: "pointer", marginBottom: 4, fontFamily: "inherit" }}>
                <div style={{ fontWeight: 600 }}>{r.title}</div>
                <div style={{ fontSize: 10, color: T.txM, marginTop: 2 }} dangerouslySetInnerHTML={{ __html: r.snippet }} />
              </button>
            ))}
          </div>
        ) : loading ? (
          <div style={{ fontSize: 12, color: T.txM }}>Loading...</div>
        ) : (
          Object.entries(sectionGroups).map(([section, items]) => (
            <div key={section} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: T.txM, textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 6, fontWeight: 700 }}>{section}</div>
              {items.map(a => (
                <button key={a.slug} onClick={() => onSelectArticle(a.slug)} style={{ display: "block", width: "100%", textAlign: "left", padding: "6px 10px", borderRadius: 4, border: "none", backgroundColor: a.slug === activeSlug ? T.accS : "transparent", color: a.slug === activeSlug ? T.acc : T.tx, fontSize: 12, cursor: "pointer", fontFamily: "inherit", fontWeight: a.slug === activeSlug ? 600 : 400 }}>
                  {a.title}
                  {a.is_public && <span style={{ fontSize: 9, marginLeft: 6, color: T.grn }}>PUB</span>}
                </button>
              ))}
            </div>
          ))
        )}
      </aside>

      <main style={{ backgroundColor: T.bg2, padding: 28, borderRadius: 8, border: `1px solid ${T.brd}` }}>
        {activeArticle ? (
          <article>
            <div style={{ fontSize: 11, color: T.txM, textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 6 }}>{activeArticle.section}</div>
            <h1 style={{ fontSize: 22, fontWeight: 700, margin: "0 0 16px", color: T.tx }}>{activeArticle.title}</h1>
            <div className="help-article-body" style={{ fontSize: 13, lineHeight: 1.7, color: T.tx }} dangerouslySetInnerHTML={{ __html: activeArticle.body_html }} />
          </article>
        ) : !loading && articles.length === 0 ? (
          <div style={{ fontSize: 13, color: T.txM, textAlign: "center", padding: 40 }}>
            No articles available for your role.
          </div>
        ) : (
          <div style={{ fontSize: 13, color: T.txM }}>Loading...</div>
        )}
      </main>
    </div>
  );
};
